/* JAWLINE RUN – FIGHTER scenery: a run through New York.
   Stage 1 Manhattan morning (brownstones, cabs) → 2 Midtown golden hour (glass towers, billboard)
   → 3 Central Park sunset (stone wall, trees) → 4 Central Park by night (Bow Bridge, lanterns).
   Each stage is painted once into offscreen strips (2 art pixels per world pixel) and scrolled with
   parallax by jawline-run-fighter.js. Cabs, leaves and fireflies are animated per frame.
   No image files: everything is drawn here. */
(function () {
  'use strict';
  if (window.JawlineRunFighterScenery) return;

  const D = 2; // art pixels per world pixel
  const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
  const hx = (h) => { const n = parseInt(h.slice(1), 16); return [(n >> 16) & 255, (n >> 8) & 255, n & 255]; };
  const mixA = (a, b, t) => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
  const css = (v) => `rgb(${clamp(v[0] | 0, 0, 255)},${clamp(v[1] | 0, 0, 255)},${clamp(v[2] | 0, 0, 255)})`;
  function rng(seed) {
    let s = seed >>> 0 || 1;
    return () => {
      s = (s + 0x6d2b79f5) | 0;
      let t = Math.imul(s ^ (s >>> 15), 1 | s);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }

  // Light of each stage: ambient multiplier, color cast, horizon haze, window light, glass reflection
  const LIGHT = [
    { amb: [0.96, 0.98, 1.02], tint: '#a8c8ea', tintAmt: 0.1, haze: '#bcd4ea', night: 0, lit: 0.03, glass: ['#e4f2fc', '#5a82ae'] },
    { amb: [1.06, 0.9, 0.76], tint: '#ff9a4a', tintAmt: 0.15, haze: '#f0b07a', night: 0, lit: 0.14, glass: ['#ffe2a2', '#a6553a'] },
    { amb: [0.9, 0.76, 0.8], tint: '#ff7a6a', tintAmt: 0.17, haze: '#d99090', night: 0.2, lit: 0.4, glass: ['#ffc4a4', '#5e3656'] },
    { amb: [0.4, 0.44, 0.66], tint: '#1a2050', tintAmt: 0.22, haze: '#283062', night: 1, lit: 0.55, glass: ['#3e4a80', '#11163a'] }
  ];

  // ---------------------------------------------------------------------------
  // Painter: small toolkit for crisp pixel painting into an offscreen canvas
  // ---------------------------------------------------------------------------
  function painter(w, h, L) {
    const cv = document.createElement('canvas');
    cv.width = w; cv.height = h;
    const c = cv.getContext('2d');
    const tint = hx(L.tint), haze = hx(L.haze);
    const P = {
      cv, c, w, h, L,
      // base color under this stage's light; k = extra brightness, z = haze toward the horizon color
      k(hex, k = 1, z = 0) {
        let v = hx(hex).map((x, i) => x * L.amb[i] * k);
        v = mixA(v, tint, L.tintAmt);
        if (z) v = mixA(v, haze, z);
        return css(v);
      },
      // emissive color (lamps, neon, lit windows): not darkened by the night
      e(hex) { return hex; },
      r(x, y, ww, hh, f) { if (f) c.fillStyle = f; c.fillRect(Math.round(x), Math.round(y), Math.round(ww), Math.round(hh)); },
      px(x, y, f) { c.fillStyle = f; c.fillRect(Math.round(x), Math.round(y), 1, 1); },
      disc(cx, cy, r, f) {
        c.fillStyle = f;
        for (let dy = -Math.ceil(r); dy <= Math.ceil(r); dy++) {
          const ww = Math.round(Math.sqrt(Math.max(0, r * r - dy * dy)));
          if (ww > 0 || Math.abs(dy) < r) c.fillRect(Math.round(cx - ww), Math.round(cy + dy), ww * 2 + 1, 1);
        }
      },
      ell(cx, cy, rx, ry, f) {
        c.fillStyle = f;
        for (let dy = -Math.ceil(ry); dy <= Math.ceil(ry); dy++) {
          const ww = Math.round(rx * Math.sqrt(Math.max(0, 1 - (dy * dy) / (ry * ry))));
          c.fillRect(Math.round(cx - ww), Math.round(cy + dy), ww * 2 + 1, 1);
        }
      },
      speck(x, y, ww, hh, f, n, R) { c.fillStyle = f; for (let i = 0; i < n; i++) c.fillRect(Math.round(x + R() * ww), Math.round(y + R() * hh), 1, 1); },
      line(x0, y0, x1, y1, f, t = 1) {
        c.fillStyle = f;
        const n = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0)) | 0;
        for (let i = 0; i <= n; i++) c.fillRect(Math.round(x0 + ((x1 - x0) * i) / (n || 1)), Math.round(y0 + ((y1 - y0) * i) / (n || 1)), t, t);
      },
      glow(cx, cy, r, rgb, a) {
        const g = c.createRadialGradient(cx, cy, 0, cx, cy, r);
        g.addColorStop(0, `rgba(${rgb},${a})`); g.addColorStop(0.45, `rgba(${rgb},${a * 0.35})`); g.addColorStop(1, `rgba(${rgb},0)`);
        c.fillStyle = g; c.fillRect(cx - r, cy - r, r * 2, r * 2);
      },
      vgrad(x, y, ww, hh, top, bot) { const a = hx(top), b = hx(bot); for (let j = 0; j < hh; j++) P.r(x, y + j, ww, 1, css(mixA(a, b, j / Math.max(1, hh - 1)))); }
    };
    return P;
  }

  // A window: frame, sky reflection with glare (or warm light at dusk/night), mullions, sill and lintel
  function windowAt(P, x, y, w, h, R, o = {}) {
    const L = P.L, frame = P.k(o.frame || '#2a1f1b'), lit = R() < (o.lit != null ? o.lit : L.lit);
    P.r(x - 1, y - 1, w + 2, h + 2, frame);
    if (lit) {
      P.vgrad(x, y, w, h, '#ffe7a2', '#e9a144');
      if (R() < 0.45) P.r(x + (R() < 0.5 ? 0 : Math.ceil(w / 2)), y, Math.floor(w / 2), h, R() < 0.5 ? '#c86a44' : '#b4924c');
    } else {
      const a = hx(L.glass[0]), b = hx(L.glass[1]);
      for (let j = 0; j < h; j++) P.r(x, y + j, w, 1, css(mixA(a, b, Math.pow(j / h, 0.8))));
      c2(P, 'rgba(255,255,255,0.32)');
      for (let j = 0; j < h; j++) { const gx = x + ((h - j) * 0.55 + (o.ph || 0)) % (w + 6) - 3; if (gx >= x && gx < x + w - 1) P.c.fillRect(Math.round(gx), y + j, 2, 1); }
      if (L.night > 0.5 && R() < 0.5) P.r(x, y, w, h, 'rgba(10,14,40,0.45)');
    }
    if (o.mull !== false) {
      P.r(x + (w >> 1), y, 1, h, frame);
      P.r(x, y + Math.round(h * 0.45), w, 1, frame);
    }
    if (o.sill) { P.r(x - 2, y + h + 1, w + 4, 2, P.k(o.sill)); P.r(x - 2, y + h + 3, w + 4, 1, P.k(o.frame || '#2a1f1b', 0.8)); }
    if (o.lintel) { P.r(x - 2, y - 5, w + 4, 4, P.k(o.lintel)); P.r(x - 2, y - 5, w + 4, 1, P.k(o.lintel, 1.15)); }
  }
  function c2(P, f) { P.c.fillStyle = f; }

  // ---------------------------------------------------------------------------
  // Manhattan buildings (across the street, about half scale)
  // ---------------------------------------------------------------------------
  const STONE = [['#7b4a37', '#5a3426', '#9a6248'], ['#8a5a44', '#66402f', '#a8745a'], ['#b39b7c', '#8a7357', '#cdb998'], ['#86402f', '#622b20', '#a85a44']];
  function brownstone(P, x, w, base, R) {
    const SH = 40, floors = 3 + (R() < 0.45 ? 1 : 0), bsm = 22, H = bsm + floors * SH + 12, top = base - H;
    const [mid, dk, hi] = STONE[(R() * STONE.length) | 0];
    P.r(x, top, w, H, P.k(mid));
    P.speck(x, top, w, H, P.k(dk), w * H * 0.03, R);
    P.speck(x, top, w, H, P.k(hi), w * H * 0.015, R);
    P.r(x, top, 1, H, P.k(hi, 1.1)); P.r(x + w - 3, top, 3, H, P.k(dk)); P.r(x + w - 1, top, 1, H, P.k(dk, 0.7));
    // rusticated basement
    const bt = base - bsm;
    P.r(x, bt, w, bsm, P.k(mid, 1.08));
    for (let yy = bt + 5; yy < base; yy += 6) P.r(x, yy, w, 1, P.k(dk));
    P.r(x, bt, w, 2, P.k(hi, 1.1));
    // columns: door column holds the stoop
    const cols = [x + w * 0.2, x + w * 0.5, x + w * 0.8], doorCol = R() < 0.5 ? 0 : 2;
    // basement windows with iron grilles
    cols.forEach((cx, i) => {
      if (i === doorCol) return;
      const wx = Math.round(cx - 6), wy = bt + 7;
      windowAt(P, wx, wy, 12, 12, R, { mull: false, lit: P.L.lit * 0.6 });
      for (let gx = wx + 1; gx < wx + 12; gx += 3) P.r(gx, wy, 1, 12, P.k('#1a1414'));
    });
    // parlor floor and upper floors
    for (let f = 0; f < floors; f++) {
      const fy = bt - (f + 1) * SH, tall = f === 0;
      cols.forEach((cx, i) => {
        const ww = 11, wh = tall ? 26 : 22, wx = Math.round(cx - ww / 2), wy = fy + (tall ? 8 : 10);
        if (f === 0 && i === doorCol) return;
        windowAt(P, wx, wy, ww, wh, R, { sill: hi, lintel: hi, ph: R() * 10 });
        if (f <= 1 && R() < 0.35) { // flower box
          P.r(wx - 2, wy + wh + 3, ww + 4, 4, P.k('#3d5a2a'));
          for (let k = 0; k < 6; k++) P.px(wx - 1 + R() * (ww + 2), wy + wh + 2 + R() * 2, P.k(['#e8506a', '#f2f0e0', '#f6b13a'][(R() * 3) | 0]));
        }
      });
    }
    // stoop: steps up to the parlor door, stone cheek walls, iron railings
    const dcx = cols[doorCol], sw = 24, sx = Math.round(dcx - sw / 2);
    for (let s = 0; s < 8; s++) {
      const sy = base - (s + 1) * 2.75, inset = s * 0.5;
      P.r(sx + inset, sy, sw - inset * 2, 2.75, P.k(mid, s % 2 ? 1.18 : 0.95));
      P.r(sx + inset, sy, sw - inset * 2, 1, P.k(hi, 1.15));
    }
    P.r(sx - 3, bt - 2, 3, bsm + 2, P.k(dk)); P.r(sx + sw, bt - 2, 3, bsm + 2, P.k(dk));
    const iron = P.k('#161214');
    for (const rx of [sx - 3, sx + sw + 1]) {
      P.r(rx, bt - 13, 2, 12, iron); P.r(rx - 1, bt - 14, 4, 2, iron);
      for (let yy = bt - 11; yy < bt; yy += 3) P.px(rx + (rx < sx ? 2 : -1), yy, iron);
    }
    // door with stone hood
    const dx = Math.round(dcx - 8), dy = bt - 30;
    P.r(dx - 4, dy - 8, 24, 6, P.k(hi, 1.1)); P.r(dx - 4, dy - 3, 24, 1, P.k(dk));
    P.r(dx - 1, dy - 1, 18, 31, P.k('#1f1410'));
    P.r(dx, dy, 16, 30, P.k('#5a3220'));
    P.r(dx + 2, dy + 2, 5, 11, P.k('#4a2818')); P.r(dx + 9, dy + 2, 5, 11, P.k('#4a2818'));
    P.r(dx + 2, dy + 16, 5, 11, P.k('#4a2818')); P.r(dx + 9, dy + 16, 5, 11, P.k('#4a2818'));
    P.r(dx, dy, 16, 1, P.k('#7a4a30'));
    P.px(dx + 13, dy + 18, P.k('#e2c070'));
    if (P.L.night > 0.1) P.glow(dcx, dy - 6, 14, '255,210,130', 0.5);
    // cornice: overhang, dentils, brackets
    P.r(x - 3, top, w + 6, 8, P.k(dk)); P.r(x - 3, top, w + 6, 2, P.k(hi, 1.1));
    for (let dx2 = x - 2; dx2 < x + w + 2; dx2 += 4) P.r(dx2, top + 8, 2, 2, P.k(dk, 0.8));
    for (let k = 0; k < 4; k++) { const bx = x + 2 + ((w - 8) * k) / 3; P.r(bx, top + 8, 3, 6, P.k(dk)); P.r(bx, top + 8, 1, 6, P.k(hi)); }
  }

  const SIGNS = ['DELI', 'PIZZA', 'CAFE', 'BAGELS', 'GYM', 'BODEGA', 'FLOWERS', 'DINER'];
  const BRICK = [['#9a4a32', '#702f1e', '#b8664a'], ['#b08a62', '#86643f', '#c9a67e'], ['#7a5d4c', '#56402f', '#957660'], ['#a2563c', '#783a26', '#c27052']];
  function apartment(P, x, w, base, R, o) {
    const SH = 38, floors = 3 + ((R() * 3) | 0), shopH = 44, H = shopH + floors * SH + 10, top = Math.max(-40, base - H);
    const [mid, dk, hi] = BRICK[(R() * BRICK.length) | 0];
    P.r(x, top, w, base - top, P.k(mid));
    c2(P, P.k(dk)); P.c.globalAlpha = 0.35;
    for (let yy = top + 2; yy < base - shopH; yy += 3) P.c.fillRect(x, yy, w, 1);
    P.c.globalAlpha = 1;
    P.speck(x, top, w, base - top, P.k(hi), w * (base - top) * 0.02, R);
    P.r(x, top, 1, base - top, P.k(hi, 1.1)); P.r(x + w - 3, top, 3, base - top, P.k(dk));
    // window grid with stone lintels, sills and AC units
    const cols = Math.max(2, Math.floor((w - 8) / 22)), step = w / cols, stone = '#cfc1a6';
    for (let f = 0; f < floors; f++) {
      const wy = base - shopH - (f + 1) * SH + 10;
      if (wy < top - 30) break;
      for (let ci = 0; ci < cols; ci++) {
        const wx = Math.round(x + step * ci + step / 2 - 6);
        windowAt(P, wx, wy, 11, 21, R, { sill: stone, lintel: stone, ph: R() * 10 });
        if (R() < 0.18) { P.r(wx + 1, wy + 15, 9, 6, P.k('#c9c6bc')); P.r(wx + 1, wy + 15, 9, 1, P.k('#e8e6de')); for (let gx = wx + 2; gx < wx + 10; gx += 2) P.r(gx, wy + 17, 1, 3, P.k('#8a877e')); }
      }
      P.r(x, wy + 28, w, 1, P.k(dk));
    }
    // fire escape over the two middle columns
    if (cols >= 3 && R() < 0.75) {
      const iron = P.k('#17131a'), fx = Math.round(x + step * (cols / 2 - 1) + 2), fw = Math.round(step * 2 - 4);
      for (let f = 0; f < floors; f++) {
        const py = base - shopH - f * SH - 6;
        if (py < top) break;
        P.r(fx - 2, py, fw + 4, 2, iron);
        P.r(fx - 2, py - 8, fw + 4, 1, iron);
        for (let xx = fx - 2; xx <= fx + fw + 2; xx += 3) P.r(xx, py - 8, 1, 8, iron);
        if (f < floors - 1 && py - SH > top) {
          const a = f % 2 ? fx + 4 : fx + fw - 6, b = f % 2 ? fx + fw - 6 : fx + 4;
          P.line(a, py, b, py - SH + 2, iron);
          P.line(a + 3, py, b + 3, py - SH + 2, iron);
          for (let k = 2; k < SH - 2; k += 4) { const t = k / SH; P.r(Math.round(a + (b - a) * t), Math.round(py - k), 4, 1, iron); }
        }
      }
      P.r(fx + fw - 10, base - shopH - 6, 1, 12, iron); P.r(fx + fw - 6, base - shopH - 6, 1, 12, iron);
    }
    // shop: sign band, striped awning, lit shop window
    const sy = base - shopH, sign = SIGNS[(R() * SIGNS.length) | 0], sc = ['#1d4a3a', '#7a1c22', '#1f2c5c', '#2a2420'][(R() * 4) | 0];
    P.r(x, sy, w, 12, P.k(sc)); P.r(x, sy, w, 1, P.k(sc, 1.5)); P.r(x, sy + 11, w, 1, P.k('#120e0e'));
    if (o.text) o.text(P.c, sign, Math.round(x + (w - (sign.length * 4 - 1) * 2) / 2), sy + 1, P.L.night > 0.1 ? '#fff2b8' : P.k('#f4ead0'), 2);
    const aw = [['#c8202e', '#f2ede2'], ['#1f6a3c', '#f2ede2'], ['#1e3a7a', '#f2ede2'], ['#e0a020', '#3a2010']][(R() * 4) | 0];
    for (let xx = x + 2; xx < x + w - 2; xx += 4) P.r(xx, sy + 12, Math.min(4, x + w - 2 - xx), 9, P.k(aw[((xx - x) / 4) % 2 | 0]));
    for (let xx = x + 2; xx < x + w - 2; xx += 4) P.r(xx + 1, sy + 21, 2, 1, P.k(aw[0], 0.8));
    P.r(x + 2, sy + 12, w - 4, 1, P.k('#ffffff', 0.9));
    P.r(x + 2, sy + 22, w - 4, 2, 'rgba(0,0,0,0.35)');
    const gx = x + 5, gy = sy + 24, gw = w - 26, gh = shopH - 28;
    P.r(gx - 1, gy - 1, gw + 2, gh + 2, P.k('#1a1414'));
    P.vgrad(gx, gy, gw, gh, P.L.night > 0.1 ? '#ffe0a0' : '#f5e2b0', P.L.night > 0.1 ? '#d88a3a' : '#b7905e');
    for (let yy = gy + 5; yy < gy + gh; yy += 6) P.r(gx, yy, gw, 1, P.k('#6a4a30'));
    for (let k = 0; k < gw / 3; k++) P.r(gx + R() * (gw - 2), gy + 1 + ((R() * (gh / 6)) | 0) * 6, 2, 3, P.k(['#d04040', '#3a7a40', '#e8c040', '#4060b0'][(R() * 4) | 0], 0.95));
    P.r(x + w - 18, gy - 1, 13, gh + 4, P.k('#1a1414')); P.r(x + w - 17, gy, 11, gh + 2, P.k('#3a4a5a')); P.r(x + w - 15, gy + gh / 2, 2, 2, P.k('#d0b060'));
    P.r(x, base - 3, w, 3, P.k('#3a3230'));
    if (P.L.night > 0.1) P.glow(x + w / 2, gy + gh / 2, w * 0.6, '255,200,120', 0.35);
    // parapet and the classic wooden water tower
    P.r(x - 1, top, w + 2, 4, P.k(dk)); P.r(x - 1, top, w + 2, 1, P.k(hi, 1.1));
    if (top > 30 && R() < 0.6) waterTower(P, x + 10 + R() * (w - 50), top, R);
  }

  function waterTower(P, x, roof, R) {
    const steel = P.k('#2a2426'), wood = '#7a5838';
    for (const lx of [x + 3, x + 12, x + 21]) P.r(lx, roof - 12, 2, 12, steel);
    P.r(x + 1, roof - 13, 24, 2, steel);
    P.line(x + 3, roof - 1, x + 21, roof - 11, steel); P.line(x + 21, roof - 1, x + 3, roof - 11, steel);
    const ty = roof - 36;
    P.r(x, ty, 26, 23, P.k(wood));
    for (let k = 0; k < 26; k += 3) P.r(x + k, ty, 1, 23, P.k(wood, 0.78));
    P.r(x, ty, 3, 23, P.k(wood, 1.2)); P.r(x + 21, ty, 5, 23, P.k(wood, 0.7));
    P.r(x - 1, ty + 5, 28, 1, steel); P.r(x - 1, ty + 15, 28, 1, steel);
    for (let j = 0; j < 11; j++) P.r(x + j * 1.2 - 1, ty - 11 + j, 28 - j * 2.4 + 2, 1, P.k('#5a4632', j < 2 ? 0.8 : 1 - j * 0.02));
    P.r(x + 12, ty - 14, 2, 4, steel);
  }

  function manhattanBlock(w, h, L, R, o) {
    const P = painter(w, h, L), base = h;
    const items = [];
    let x = 0;
    while (x < w) {
      const t = R();
      let bw, kind;
      if (items.length && items[items.length - 1].kind !== 'gap' && t < 0.12 && !items.some((q) => q.kind === 'gap')) { kind = 'gap'; bw = 70; }
      else if (t < 0.55) { kind = 'brown'; bw = 74 + ((R() * 16) | 0); }
      else { kind = 'apt'; bw = 92 + ((R() * 40) | 0); }
      items.push({ kind, x, w: bw });
      x += bw;
    }
    const last = items[items.length - 1];
    last.w -= x - w; if (last.w < 60) { items.pop(); items[items.length - 1].w += last.w; }
    for (const it of items) {
      if (it.kind === 'brown') brownstone(P, it.x, it.w, base, R);
      else if (it.kind === 'apt') apartment(P, it.x, it.w, base, R, o);
    }
    return P.cv;
  }

  // ---------------------------------------------------------------------------
  // Midtown: glass towers, an art-deco tower and the JAWLINER billboard
  // ---------------------------------------------------------------------------
  function glassTower(P, x, w, base, R) {
    const L = P.L, lobby = 44, g0 = hx(L.glass[0]), g1 = hx(L.glass[1]), ph = R() * 6;
    for (let y = 0; y < base - lobby; y++) {
      const t = clamp(0.15 + (y / base) * 0.85 + Math.sin(y * 0.05 + ph) * 0.08, 0, 1);
      P.r(x, y, w, 1, css(mixA(g0, g1, t)));
    }
    c2(P, 'rgba(255,255,255,0.16)');
    for (let y = 0; y < base - lobby; y++) { const gx = (y * 0.7 + ph * 40) % (w * 2.4) - w * 0.4; if (gx > 0 && gx < w - 10) P.c.fillRect(x + gx, y, 10, 1); }
    const mull = P.k('#24262e', 0.9);
    for (let xx = x + 5; xx < x + w; xx += 10) P.r(xx, 0, 1, base - lobby, mull);
    for (let yy = base - lobby - 12; yy > 0; yy -= 13) P.r(x, yy, w, 2, P.k('#2e3038'));
    if (L.lit > 0.1) for (let yy = base - lobby - 12; yy > 0; yy -= 13) for (let xx = x + 5; xx < x + w - 10; xx += 10) if (R() < L.lit * 0.4) P.r(xx + 1, yy - 11, 9, 11, `rgba(255,214,140,${0.35 + L.night * 0.4})`);
    P.r(x, 0, 2, base, P.k('#1c1e26')); P.r(x + w - 2, 0, 2, base, P.k('#1c1e26'));
    // lobby: granite piers, warm lobby light, revolving door and canopy
    const ly = base - lobby;
    P.r(x, ly, w, lobby, P.k('#3a3636'));
    P.r(x, ly, w, 3, P.k('#5a5450'));
    for (let xx = x + 4; xx < x + w - 12; xx += 22) {
      P.vgrad(xx + 5, ly + 8, 14, lobby - 10, '#ffeab8', '#d89a50');
      P.r(xx + 5, ly + 8, 14, 1, '#fff6d8');
      P.r(xx + 11, ly + 8, 1, lobby - 10, P.k('#2a2626'));
    }
    const cx = x + w / 2;
    P.r(cx - 22, ly + 4, 44, 4, P.k('#141214')); P.r(cx - 22, ly + 4, 44, 1, P.k('#b0a080'));
    if (L.night > 0.1 || L.lit > 0.1) P.glow(cx, ly + 26, 40, '255,210,140', 0.3);
  }

  function decoTower(P, x, w, base, R) {
    const lime = '#c9b897', dk = '#9a8a6e', hi = '#e2d4b4';
    P.r(x, 0, w, base, P.k(lime));
    for (let xx = x + 3; xx < x + w - 3; xx += 12) {
      P.r(xx, 0, 4, base - 40, P.k(hi));
      P.r(xx + 4, 0, 1, base - 40, P.k(dk));
      for (let yy = 8; yy < base - 48; yy += 16) windowAt(P, xx + 6, yy, 5, 10, R, { mull: false, frame: '#3a3228' });
    }
    P.r(x + w - 4, 0, 4, base, P.k(dk));
    const ly = base - 40;
    P.r(x, ly, w, 40, P.k('#5a4a3a')); P.r(x, ly, w, 3, P.k('#b89a5a'));
    for (let xx = x + 8; xx < x + w - 16; xx += 26) { P.r(xx, ly + 10, 16, 30, P.k('#1e1a16')); P.vgrad(xx + 1, ly + 11, 14, 28, '#f8dc9a', '#c88a3e'); P.r(xx + 7, ly + 11, 1, 28, P.k('#1e1a16')); }
  }

  function billboard(P, x, w, base, R, o) {
    // dark steel building carrying a giant lit billboard
    P.r(x, 0, w, base, P.k('#34303a'));
    for (let yy = 6; yy < base - 40; yy += 14) for (let xx = x + 6; xx < x + w - 8; xx += 12) windowAt(P, xx, yy, 6, 9, R, { mull: false, frame: '#1e1c22' });
    P.r(x + w - 4, 0, 4, base, P.k('#24222a'));
    const bw = w - 16, bh = Math.round(bw * 0.52), bx = x + 8, by = base - 64 - bh;
    // frame, catwalk, lights
    P.r(bx - 3, by - 3, bw + 6, bh + 6, P.k('#121116'));
    const acc = o.accent || '#c6ff3d';
    P.vgrad(bx, by, bw, bh, '#14161e', '#0a0b10');
    const text = o.brand || 'JAWLINER', sc = Math.max(2, Math.floor((bw - 10) / (text.length * 4)));
    const tw = (text.length * 4 - 1) * sc;
    o.text(P.c, text, Math.round(bx + (bw - tw) / 2), by + 8, acc, sc, '#000000');
    const tag = 'CHEW. TRAIN. WIN.';
    o.text(P.c, tag, Math.round(bx + (bw - (tag.length * 4 - 1)) / 2), by + 8 + sc * 5 + 6, '#ffffff', 1);
    (o.pouches || []).slice(0, 4).forEach((img, i) => {
      if (!img || !img.complete || !img.naturalWidth) return;
      const pw = Math.round(bh * 0.36), ph = Math.round(pw * 1.13), px = Math.round(bx + 8 + i * ((bw - 16 - pw) / 3)), py = by + bh - ph - 5;
      P.c.imageSmoothingEnabled = false; P.c.drawImage(img, px, py, pw, ph);
    });
    P.r(bx, by, bw, 1, acc); P.r(bx, by + bh - 1, bw, 1, acc);
    P.r(bx - 3, by + bh + 3, bw + 6, 2, P.k('#121116'));
    for (let lx = bx + 6; lx < bx + bw; lx += 18) {
      P.r(lx, by + bh + 1, 4, 3, P.k('#222028'));
      c2(P, 'rgba(255,240,200,0.10)');
      for (let j = 0; j < 22; j++) P.c.fillRect(lx + 2 - j * 0.35, by + bh - j, 1 + j * 0.7, 1);
    }
    P.glow(bx + bw / 2, by + bh / 2, bw * 0.75, '198,255,61', 0.12);
    // lobby
    const ly = base - 40;
    P.r(x, ly, w, 40, P.k('#2a2830'));
    for (let xx = x + 6; xx < x + w - 12; xx += 20) P.vgrad(xx, ly + 8, 14, 32, '#ffe7b0', '#cf8c46');
  }

  function midtownBlock(w, h, L, R, o) {
    const P = painter(w, h, L), base = h;
    const seq = [];
    let x = 0, i = 0;
    const order = ['glass', 'deco', 'board', 'glass', 'deco', 'glass'];
    while (x < w) {
      const kind = order[i++ % order.length];
      const bw = kind === 'board' ? 200 : kind === 'glass' ? 120 + ((R() * 50) | 0) : 104 + ((R() * 40) | 0);
      seq.push({ kind, x, w: bw }); x += bw + (R() < 0.3 ? 14 : 0);
    }
    const last = seq[seq.length - 1]; last.w = Math.max(40, w - last.x);
    for (const s of seq) {
      if (s.x + s.w > w) s.w = w - s.x;
      if (s.w < 40) continue;
      if (s.kind === 'glass') glassTower(P, s.x, s.w, base, R);
      else if (s.kind === 'deco') decoTower(P, s.x, s.w, base, R);
      else billboard(P, s.x, s.w, base, R, o);
    }
    return P.cv;
  }

  // ---------------------------------------------------------------------------
  // Far skyline with landmarks
  // ---------------------------------------------------------------------------
  function farTower(P, x, w, h, base, R, z) {
    const L = P.L, top = base - h, body = P.k('#5c6680', 1, z), lit = P.k('#7c88a2', 1.05, z), dark = P.k('#3e465c', 1, z);
    P.r(x, top, w, h, body);
    P.r(x, top, Math.max(2, (w * 0.3) | 0), h, lit);
    P.r(x + w - 2, top, 2, h, dark);
    const roof = R();
    if (roof < 0.25) { P.r(x + w * 0.3, top - 6, w * 0.4, 6, body); P.r(x + w * 0.45, top - 16, 1, 10, dark); }
    else if (roof < 0.45) { for (let j = 0; j < 8; j++) P.r(x + j, top - 8 + j, w - j * 2, 1, body); }
    else if (roof < 0.6) P.r(x + w / 2, top - 14, 1, 14, dark);
    for (let yy = top + 4; yy < base - 2; yy += 4) for (let xx = x + 2; xx < x + w - 2; xx += 3) {
      if (L.night > 0.3) { if (R() < 0.28) P.px(xx, yy, R() < 0.8 ? '#ffd27a' : '#cfe4ff'); }
      else if (R() < 0.5) P.px(xx, yy, R() < 0.5 ? dark : lit);
    }
  }

  function empireState(P, cx, base, z) {
    const tiers = [[80, 52], [62, 112], [44, 46], [30, 20], [20, 14], [11, 12]];
    const L = P.L, stone = P.k('#b4ad9c', 1, z), hi = P.k('#d4cdb8', 1.05, z), dk = P.k('#7c7666', 1, z);
    let y = base;
    for (const [w, h] of tiers) {
      const x = Math.round(cx - w / 2);
      y -= h;
      P.r(x, y, w, h, stone);
      for (let xx = x + 2; xx < x + w - 1; xx += 4) P.r(xx, y, 1, h, dk);
      P.r(x, y, Math.round(w * 0.28), h, hi);
      P.r(x + w - 2, y, 2, h, dk);
      if (L.night > 0.3) for (let yy = y + 3; yy < y + h; yy += 4) for (let xx = x + 3; xx < x + w - 2; xx += 4) if (Math.random() < 0.3) P.px(xx, yy, '#ffd27a');
    }
    P.r(cx - 3, y - 22, 6, 22, stone); P.r(cx - 3, y - 22, 2, 22, hi);
    P.r(cx - 1, y - 50, 2, 28, dk);
    if (L.night > 0.3 || L.lit > 0.3) { P.r(cx - 10, y - 2, 20, 14, 'rgba(198,255,61,0.55)'); P.glow(cx, y - 10, 34, '198,255,61', 0.35); P.px(cx, y - 50, '#ff4040'); }
  }

  function chrysler(P, cx, base, z) {
    const L = P.L, steel = P.k('#d6dae2', 1.08, z), body = P.k('#9aa0ac', 1, z), dk = P.k('#5e6472', 1, z);
    const sw = 40, sh = 190, x = cx - sw / 2, top = base - sh;
    P.r(x, top, sw, sh, body);
    P.r(x, top, 12, sh, P.k('#b4bac6', 1, z)); P.r(x + sw - 3, top, 3, sh, dk);
    for (let xx = x + 3; xx < x + sw - 2; xx += 4) P.r(xx, top + 4, 1, sh - 4, dk);
    let y = top, r = 20;
    for (let k = 0; k < 5; k++) {
      // stacked crown arches with sunburst windows
      const ar = r - k * 3.4;
      for (let dy = 0; dy <= ar; dy++) {
        const ww = Math.round(Math.sqrt(ar * ar - dy * dy));
        P.r(cx - ww, y - dy, ww * 2, 1, dy === Math.round(ar) ? steel : body);
        P.px(cx - ww, y - dy, steel); P.px(cx + ww - 1, y - dy, steel);
      }
      for (let a = 0.35; a < Math.PI - 0.3; a += 0.42) {
        const tx = cx + Math.cos(a) * ar * 0.62, ty = y - Math.sin(a) * ar * 0.62;
        P.r(tx - 1, ty - 1, 2, 3, L.night > 0.3 || L.lit > 0.1 ? '#fff0c0' : dk);
      }
      y -= ar * 0.62;
    }
    for (let j = 0; j < 44; j++) P.r(cx - Math.max(0.5, 2.6 - j * 0.06), y - j, Math.max(1, 5.2 - j * 0.12), 1, steel);
    if (L.night > 0.3 || L.lit > 0.1) P.glow(cx, y + 8, 30, '255,236,180', 0.3);
  }

  function supertall(P, x, w, h, base, R, z) {
    const L = P.L, body = P.k('#4e586e', 1, z), lit = P.k('#6e7a92', 1.05, z), top = base - h;
    P.r(x, top, w, h, body); P.r(x, top, 3, h, lit);
    for (let yy = top + 3; yy < base; yy += 5) for (let xx = x + 2; xx < x + w - 2; xx += 4) P.r(xx, yy, 2, 3, L.night > 0.3 ? (R() < 0.3 ? '#ffd27a' : P.k('#232a40', 1, z)) : P.k('#38415a', 1, z));
    P.r(x + w / 2, top - 10, 1, 10, body);
    if (L.night > 0.3) P.px(x + w / 2, top - 10, '#ff4040');
  }

  function skyline(w, h, L, R, opt) {
    const P = painter(w, h, L), base = h;
    // two depth rows: very far (hazier, lower) then far
    for (const [z, minH, varH, step] of [[0.62, 40, 70, 0.9], [0.42, 60, 120, 1]]) {
      let x = -10;
      while (x < w + 10) {
        const bw = 18 + ((R() * 46) | 0), bh = minH * opt.hScale + R() * varH * opt.hScale;
        farTower(P, x, bw, bh, base, R, z);
        x += Math.round(bw * step) + ((R() * 6) | 0);
      }
    }
    for (const lm of opt.landmarks) {
      if (lm.t === 'empire') empireState(P, lm.x * w, base, 0.3);
      else if (lm.t === 'chrysler') chrysler(P, lm.x * w, base, 0.3);
      else if (lm.t === 'super') supertall(P, lm.x * w, lm.w, lm.h, base, R, 0.32);
    }
    return P.cv;
  }

  // ---------------------------------------------------------------------------
  // Central Park: trees, stone wall, lamps, benches, lake and Bow Bridge
  // ---------------------------------------------------------------------------
  function tree(P, cx, base, R, o) {
    const L = P.L, night = L.night > 0.5;
    const G = o.autumn ? ['#3a2a18', '#7a3a1a', '#b8601e', '#e8a040'] : ['#16301c', '#24502a', '#3e7432', '#7aa242'];
    const trunkH = o.trunk, tw = o.tw || 6, bark = P.k('#4a3424'), barkDk = P.k('#2a1c14');
    P.r(cx - tw / 2, base - trunkH, tw, trunkH, bark); P.r(cx + tw / 2 - 2, base - trunkH, 2, trunkH, barkDk); P.r(cx - tw / 2, base - trunkH, 1, trunkH, P.k('#6a4c34'));
    P.line(cx, base - trunkH * 0.6, cx - o.r * 0.5, base - trunkH - o.r * 0.3, bark, 2);
    P.line(cx, base - trunkH * 0.7, cx + o.r * 0.55, base - trunkH - o.r * 0.4, bark, 2);
    const ccx = cx, ccy = base - trunkH - o.r * 0.55, blobs = [];
    const n = 9 + ((R() * 6) | 0);
    for (let i = 0; i < n; i++) {
      const a = R() * Math.PI * 2, d = Math.sqrt(R());
      blobs.push([ccx + Math.cos(a) * o.r * 0.95 * d, ccy + Math.sin(a) * o.r * 0.7 * d, o.r * (0.38 + R() * 0.22)]);
    }
    blobs.sort((p, q) => p[1] - q[1]);
    const lx = -0.32, ly = -0.38;
    for (const [bx, by, br] of blobs) P.disc(bx, by, br + 1.5, P.k('#0c1a10', 0.9));
    for (const [bx, by, br] of blobs) P.disc(bx, by, br, P.k(G[0]));
    for (const [bx, by, br] of blobs) P.disc(bx + lx * br * 0.35, by + ly * br * 0.35, br * 0.78, P.k(G[1]));
    for (const [bx, by, br] of blobs) P.disc(bx + lx * br * 0.6, by + ly * br * 0.6, br * 0.48, P.k(G[2]));
    for (const [bx, by, br] of blobs) if (R() < 0.7) P.disc(bx + lx * br * 0.85, by + ly * br * 0.85, br * 0.2, P.k(G[3], night ? 0.8 : 1));
    // leaf texture
    P.speck(ccx - o.r, ccy - o.r * 0.8, o.r * 2, o.r * 1.5, P.k(G[3]), o.r * 3, R);
    P.speck(ccx - o.r, ccy - o.r * 0.8, o.r * 2, o.r * 1.5, P.k(G[0], 0.8), o.r * 3, R);
  }

  function parkTrees(w, h, L, R, o) {
    const P = painter(w, h, L), base = h - (o.water || 0);
    // a band of bushes at the back, then trees of different sizes (drawn wrapped for a seamless strip)
    const trees = [];
    let x = 10;
    while (x < w) { const r = 34 + R() * 34; trees.push({ x, r, trunk: 34 + R() * 50, autumn: o.autumn && R() < 0.45, tw: 6 + ((R() * 4) | 0) }); x += r * (1.15 + R() * 0.7); }
    if (o.bridge) {
      // keep the middle free for the bridge
      for (const t of trees) if (Math.abs(t.x - w * 0.5) < 150) t.skip = true;
    }
    const bush = (bx) => { for (let k = 0; k < 4; k++) P.disc(bx + k * 9, base - 10 - (k % 2) * 4, 10, P.k(k % 2 ? '#24502a' : '#1c3e22')); };
    for (let bx = -10; bx < w + 10; bx += 34) bush(bx);
    for (const t of trees) {
      if (t.skip) continue;
      for (const dx of [0, -w, w]) tree(P, t.x + dx, base, R, { r: t.r, trunk: t.trunk, autumn: t.autumn, tw: t.tw });
    }
    if (o.water) {
      // lake under the trees: dark water with sky and lantern reflections
      const wy = base;
      P.vgrad(0, wy, w, o.water, L.night > 0.5 ? '#1a2250' : '#c27a7a', L.night > 0.5 ? '#0c1030' : '#5a3a52');
      for (let k = 0; k < w / 6; k++) P.r(R() * w, wy + 2 + R() * (o.water - 4), 4 + R() * 10, 1, L.night > 0.5 ? 'rgba(160,180,255,0.25)' : 'rgba(255,220,200,0.3)');
    }
    if (o.bridge) bowBridge(P, w * 0.5, base, 300, L, R);
    return P.cv;
  }

  // Bow Bridge: a flat cast-iron arch with a cream balustrade of circles, mirrored in the lake
  function bowBridge(P, cx, base, span, L, R) {
    const iron = P.k('#e6dcc4', 1.05), ironDk = P.k('#a89c84'), deckY = base - 30, x0 = cx - span / 2;
    // arch underside
    for (let i = 0; i <= span; i++) {
      const u = i / span, y = deckY + 6 + Math.round((1 - Math.sin(u * Math.PI)) * 22);
      P.r(x0 + i, deckY + 4, 1, y - deckY - 4, ironDk);
      P.px(x0 + i, y, iron);
    }
    // deck and balustrade
    P.r(x0 - 6, deckY, span + 12, 5, iron); P.r(x0 - 6, deckY + 4, span + 12, 1, ironDk);
    P.r(x0 - 6, deckY - 16, span + 12, 2, iron);
    for (let xx = x0 - 2; xx < x0 + span + 2; xx += 8) {
      P.r(xx, deckY - 14, 1, 14, iron);
      for (let dy = -3; dy <= 3; dy++) { const ww = Math.round(Math.sqrt(9 - dy * dy)); P.px(xx + 4 - ww, deckY - 8 + dy, iron); P.px(xx + 4 + ww, deckY - 8 + dy, iron); }
    }
    // stone abutments with planters
    for (const ax of [x0 - 18, x0 + span + 2]) { P.r(ax, deckY - 4, 16, 34, P.k('#8a8070')); P.r(ax, deckY - 4, 16, 2, P.k('#b4aa98')); P.ell(ax + 8, deckY - 8, 7, 4, P.k('#2a5a2a')); }
    // lanterns on the bridge
    for (const lx of [x0 + span * 0.25, x0 + span * 0.75]) {
      P.r(lx, deckY - 30, 2, 14, P.k('#1a1a20')); P.r(lx - 2, deckY - 36, 6, 7, L.night > 0.3 ? '#ffe8a0' : P.k('#d8d0b0'));
      if (L.night > 0.3) P.glow(lx + 1, deckY - 32, 26, '255,220,140', 0.55);
    }
    // reflection in the water below
    c2(P, L.night > 0.5 ? 'rgba(220,210,180,0.18)' : 'rgba(255,240,220,0.22)');
    for (let i = 0; i <= span; i += 2) {
      const u = i / span, y = base + 2 + Math.round((Math.sin(u * Math.PI)) * 10);
      P.c.fillRect(x0 + i + Math.sin(i * 0.4) * 1.5, y, 2, 1);
    }
  }

  function stoneWall(w, h, L, R) {
    const P = painter(w, h, L), base = h, wallH = 26, top = base - wallH;
    // bushes peeking over the wall
    for (let bx = -6; bx < w + 6; bx += 14) P.disc(bx, top - 2, 7 + R() * 4, P.k(R() < 0.5 ? '#24502a' : '#1c3e22'));
    P.r(0, top, w, wallH, P.k('#6e665c'));
    for (let row = 0, y = top + 4; y < base; y += 7, row++) {
      let x = row % 2 ? -6 : 0;
      while (x < w) {
        const sw = 10 + ((R() * 12) | 0), tone = 0.85 + R() * 0.3;
        P.r(x + 1, y + 1, sw - 1, 6, P.k('#8e8476', tone));
        P.r(x + 1, y + 1, sw - 1, 1, P.k('#b0a694', tone));
        P.speck(x + 1, y + 2, sw - 2, 4, P.k('#6a6258'), 3, R);
        x += sw;
      }
    }
    P.r(0, top - 1, w, 5, P.k('#a49a88')); P.r(0, top - 1, w, 1, P.k('#c8bfac')); P.r(0, top + 4, w, 1, P.k('#4a443c'));
    return P.cv;
  }

  function lakeEdge(w, h, L, R) {
    const P = painter(w, h, L), base = h, top = 0;
    P.vgrad(0, top, w, h - 10, '#1e2858', '#0a0e2c');
    for (let k = 0; k < w / 5; k++) P.r(R() * w, 2 + R() * (h - 16), 3 + R() * 8, 1, 'rgba(150,170,255,0.22)');
    for (let k = 0; k < 6; k++) { const x = R() * w; for (let j = 2; j < h - 12; j += 2) P.r(x + Math.sin(j) * 1.5, j, 2, 1, `rgba(255,220,140,${0.5 - j / h * 0.4})`); }
    // low cast-iron hoop fence along the path
    const iron = P.k('#121218');
    P.r(0, base - 10, w, 10, P.k('#2a3a24'));
    for (let x = 0; x < w; x += 10) { for (let a = 0; a <= 10; a++) { const yy = base - 4 - Math.round(Math.sin((a / 10) * Math.PI) * 6); P.px(x + a, yy, iron); } P.r(x, base - 4, 1, 4, iron); }
    return P.cv;
  }

  function street(w, h, L, R) {
    // the street between our sidewalk and the buildings: far sidewalk, asphalt, lane lines, near curb
    const P = painter(w, h, L), base = h;
    P.r(0, 0, w, 8, P.k('#b9b3a6')); P.r(0, 0, w, 1, P.k('#d8d2c4'));
    for (let x = 0; x < w; x += 24) P.r(x, 1, 1, 7, P.k('#8e887c'));
    P.r(0, 8, w, 2, P.k('#6e6a64'));
    P.vgrad(0, 10, w, base - 14, '#4a4a50', '#36363c');
    P.speck(0, 10, w, base - 14, P.k('#5c5c62'), w * 1.2, R);
    P.speck(0, 10, w, base - 14, P.k('#2a2a2e'), w * 1.2, R);
    const my = 10 + Math.round((base - 14) * 0.45);
    for (let x = 0; x < w; x += 40) { P.r(x, my, 26, 1, P.k('#e8c030')); P.r(x, my + 2, 26, 1, P.k('#e8c030')); }
    for (let k = 0; k < 2; k++) { const mx = 60 + k * (w / 2); P.ell(mx, base - 9, 7, 2, P.k('#2a2a2c')); P.r(mx - 5, base - 9, 10, 1, P.k('#4a4a4e')); }
    P.r(0, base - 4, w, 4, P.k('#8a857c')); P.r(0, base - 4, w, 1, P.k('#c8c2b6'));
    return P.cv;
  }

  // ---------------------------------------------------------------------------
  // Street furniture right behind the runner
  // ---------------------------------------------------------------------------
  function lampNYC(P, x, base, L) {
    const pole = P.k('#2c3a34'), hi = P.k('#4e6a5c');
    P.r(x - 4, base - 12, 9, 12, pole); P.r(x - 4, base - 12, 2, 12, hi); P.r(x - 5, base - 2, 11, 2, pole);
    P.r(x - 1, base - 150, 3, 140, pole); P.r(x - 1, base - 150, 1, 140, hi);
    for (let i = 0; i < 26; i++) P.r(x + 1 + i, base - 150 - Math.round(Math.sin((i / 26) * Math.PI * 0.6) * 9), 2, 2, pole);
    P.r(x + 22, base - 154, 12, 4, pole); P.r(x + 23, base - 150, 10, 2, L.night > 0.1 ? '#fff0c0' : P.k('#e8e2d0'));
    if (L.night > 0.1) P.glow(x + 28, base - 146, 44, '255,226,160', 0.45);
  }
  function hydrant(P, x, base) {
    const red = '#c82a24';
    P.r(x - 6, base - 3, 12, 3, P.k('#7a1a16'));
    P.r(x - 4, base - 18, 8, 15, P.k(red)); P.r(x - 4, base - 18, 2, 15, P.k('#f05040'));
    P.r(x - 7, base - 13, 14, 4, P.k(red)); P.r(x - 5, base - 21, 10, 3, P.k('#a01e1a')); P.r(x - 2, base - 23, 4, 2, P.k('#a01e1a'));
  }
  function trashCan(P, x, base) {
    const g = P.k('#3a5a3a'), dk = P.k('#1e301e');
    P.r(x - 8, base - 24, 16, 24, dk);
    for (let xx = x - 7; xx < x + 8; xx += 3) P.r(xx, base - 23, 1, 22, g);
    for (let yy = base - 22; yy < base; yy += 5) P.r(x - 8, yy, 16, 1, g);
    P.r(x - 9, base - 25, 18, 2, g);
  }
  function newsBoxes(P, x, base) {
    [['#1e4aa0', 0], ['#d22a2a', 14], ['#f0c020', 28]].forEach(([c, dx]) => {
      P.r(x + dx, base - 26, 12, 26, P.k(c)); P.r(x + dx, base - 26, 2, 26, P.k(c, 1.3)); P.r(x + dx + 2, base - 22, 8, 8, P.k('#d8e0e8')); P.r(x + dx + 2, base - 22, 8, 1, P.k('#ffffff'));
    });
  }
  function mailbox(P, x, base) {
    const b = '#1d3f8a';
    P.r(x - 9, base - 30, 18, 24, P.k(b)); P.ell(x, base - 30, 9, 4, P.k(b)); P.r(x - 9, base - 30, 3, 24, P.k('#3a62b8'));
    P.r(x - 5, base - 26, 10, 2, P.k('#0e1e44')); P.r(x - 8, base - 6, 3, 6, P.k('#0e1e44')); P.r(x + 5, base - 6, 3, 6, P.k('#0e1e44'));
  }
  function streetTree(P, x, base, R) {
    tree(P, x, base - 4, R, { r: 26, trunk: 70, tw: 4 });
    const iron = P.k('#1a1a1a');
    P.r(x - 14, base - 10, 28, 1, iron); for (let xx = x - 14; xx <= x + 14; xx += 4) P.r(xx, base - 10, 1, 10, iron);
  }
  function trafficLight(P, x, base, L) {
    const pole = P.k('#2a2a2a');
    P.r(x - 1, base - 130, 3, 130, pole); P.r(x - 4, base - 6, 9, 6, pole);
    P.r(x + 2, base - 130, 34, 3, pole);
    P.r(x + 28, base - 127, 10, 26, P.k('#1a1a1a')); P.disc(x + 33, base - 122, 3, '#ff4030'); P.disc(x + 33, base - 114, 3, P.k('#5a4a1a')); P.disc(x + 33, base - 106, 3, P.k('#1a4a2a'));
    P.r(x + 3, base - 96, 14, 12, P.k('#141414')); P.r(x + 5, base - 94, 10, 8, '#ff7a30');
    P.r(x + 2, base - 70, 26, 7, P.k('#1e6a3a')); P.r(x + 2, base - 70, 26, 1, P.k('#e8f0e8'));
  }
  function hotdogCart(P, x, base, R) {
    P.r(x - 18, base - 30, 36, 22, P.k('#c8ccd4')); P.r(x - 18, base - 30, 36, 2, P.k('#eef0f4')); P.r(x - 18, base - 22, 36, 4, P.k('#2a5ab0'));
    P.disc(x - 10, base - 5, 5, P.k('#1a1a1a')); P.disc(x + 10, base - 5, 5, P.k('#1a1a1a'));
    P.r(x - 1, base - 70, 2, 40, P.k('#3a3a3a'));
    for (let j = 0; j < 12; j++) for (let i = -30 + j * 2; i < 30 - j * 2; i += 6) P.r(x + i, base - 70 - 12 + j, 6, 1, P.k(((i + 30) / 6) % 2 ? '#e8e0c8' : '#2a6ad0'));
  }
  function subway(P, x, base, L) {
    const g = P.k('#1e5a3a');
    for (const dx of [-24, 24]) {
      P.r(x + dx - 1, base - 48, 3, 48, g); P.disc(x + dx, base - 54, 6, L.night > 0.1 || L.lit > 0.1 ? '#8aff9a' : P.k('#5ac07a'));
      if (L.night > 0.1) P.glow(x + dx, base - 54, 20, '140,255,160', 0.45);
    }
    P.r(x - 24, base - 22, 48, 2, g); for (let xx = x - 22; xx < x + 24; xx += 4) P.r(xx, base - 20, 1, 20, g);
  }
  function parkLamp(P, x, base, L) {
    const pole = P.k('#1c2420'), hi = P.k('#3c4c44');
    P.r(x - 5, base - 16, 11, 16, pole); P.r(x - 5, base - 16, 2, 16, hi); P.r(x - 6, base - 3, 13, 3, pole);
    P.r(x - 1, base - 92, 3, 78, pole); P.r(x - 1, base - 92, 1, 78, hi);
    P.r(x - 6, base - 96, 13, 3, pole);
    const lamp = L.night > 0.1 ? '#fff2c4' : P.k('#e6e0cc', 1.1);
    P.ell(x + 0.5, base - 106, 6, 9, P.k('#1c2420')); P.ell(x + 0.5, base - 106, 5, 8, lamp); P.r(x - 2, base - 117, 5, 3, pole); P.r(x, base - 121, 1, 4, pole);
    if (L.night > 0.1) P.glow(x, base - 106, 50, '255,226,150', 0.55);
  }
  function bench(P, x, base) {
    const wood = '#5a8a3a', iron = P.k('#1a1e1a');
    for (const dx of [2, 50]) { P.r(x + dx, base - 18, 3, 18, iron); }
    for (let k = 0; k < 3; k++) { P.r(x, base - 32 + k * 5, 56, 3, P.k(wood, 0.95 + k * 0.05)); P.r(x, base - 32 + k * 5, 56, 1, P.k('#8aba5a')); }
    P.r(x - 2, base - 18, 60, 3, P.k(wood)); P.r(x - 2, base - 18, 60, 1, P.k('#8aba5a'));
  }

  function props(w, h, L, R, kind) {
    const P = painter(w, h, L), base = h;
    const at = (f) => Math.round(f * w);
    if (kind === 'manhattan') {
      lampNYC(P, at(0.06), base, L); streetTree(P, at(0.28), base, R); hydrant(P, at(0.4), base); newsBoxes(P, at(0.5), base);
      lampNYC(P, at(0.56), base, L); mailbox(P, at(0.72), base); trashCan(P, at(0.86), base); streetTree(P, at(0.94), base, R);
    } else if (kind === 'midtown') {
      lampNYC(P, at(0.04), base, L); trafficLight(P, at(0.22), base, L); hotdogCart(P, at(0.42), base, R);
      subway(P, at(0.62), base, L); lampNYC(P, at(0.54), base, L); trashCan(P, at(0.8), base); hydrant(P, at(0.92), base);
    } else {
      parkLamp(P, at(0.05), base, L); bench(P, at(0.16), base); trashCan(P, at(0.34), base); parkLamp(P, at(0.42), base, L);
      bench(P, at(0.56), base); parkLamp(P, at(0.78), base, L); bench(P, at(0.86), base);
    }
    return P.cv;
  }

  function cab(L) {
    const P = painter(92, 36, L), y = yb => yb;
    const yel = '#f2c22e', dk = P.k('#1a1610');
    P.r(4, 14, 86, 15, P.k(yel)); P.r(4, 14, 86, 2, P.k('#fff0a0')); P.r(4, 26, 86, 3, P.k('#b08a1a'));
    P.r(2, 16, 4, 12, P.k(yel, 0.9)); P.r(86, 18, 5, 10, P.k(yel, 0.9));
    for (let j = 0; j < 10; j++) P.r(24 + j * 0.8, 4 + j, 44 - j * 0.4 + j * 0.8, 1, P.k(yel));
    P.r(28, 6, 17, 8, P.k(L.glass[1])); P.r(48, 6, 16, 8, P.k(L.glass[1])); P.r(28, 6, 17, 2, P.k(L.glass[0])); P.r(48, 6, 16, 2, P.k(L.glass[0]));
    P.r(46, 5, 2, 9, P.k(yel)); P.r(40, 0, 14, 4, '#fff6d0'); P.r(40, 3, 14, 1, dk);
    P.r(46, 15, 1, 12, dk); P.r(10, 20, 74, 1, 'rgba(0,0,0,0.25)');
    for (let k = 0; k < 5; k++) P.r(52 + k * 4, 20, 2, 2, k % 2 ? '#111' : '#f4f4f4');
    for (const wx of [20, 72]) { P.disc(wx, 29, 7, dk); P.disc(wx, 29, 3, P.k('#9a9a9a')); P.disc(wx, 29, 1, P.k('#4a4a4a')); }
    P.r(86, 17, 4, 3, '#fff6c0'); P.r(2, 17, 3, 3, '#ff4a3a');
    if (L.night > 0.1) { P.glow(92, 18, 14, '255,240,190', 0.6); }
    return P.cv;
  }

  // ---------------------------------------------------------------------------
  // Stage layouts: layers from back to front (par = parallax, y = bottom above the floor's back edge)
  // ---------------------------------------------------------------------------
  const STAGES = [
    { seed: 11, light: 0, cabs: true, build(L, R, o) {
      return [
        { cv: skyline(1800, 360, L, R, { hScale: 1, landmarks: [{ t: 'empire', x: 0.62 }, { t: 'super', x: 0.2, w: 18, h: 250 }] }), par: 0.1, y: 16 },
        { cv: manhattanBlock(1400, 400, L, R, o), par: 0.42, y: 16 },
        { cv: street(600, 34, L, R), par: 0.8, y: 0, fx: 'cabs' },
        { cv: props(1280, 240, L, R, 'manhattan'), par: 0.94, y: -1 }
      ];
    } },
    { seed: 23, light: 1, cabs: true, build(L, R, o) {
      return [
        { cv: skyline(1800, 360, L, R, { hScale: 1.15, landmarks: [{ t: 'chrysler', x: 0.3 }, { t: 'empire', x: 0.78 }] }), par: 0.1, y: 16 },
        { cv: midtownBlock(1400, 460, L, R, o), par: 0.42, y: 16 },
        { cv: street(600, 34, L, R), par: 0.8, y: 0, fx: 'cabs' },
        { cv: props(1280, 240, L, R, 'midtown'), par: 0.94, y: -1 }
      ];
    } },
    { seed: 37, light: 2, leaves: true, build(L, R, o) {
      return [
        { cv: skyline(1800, 360, L, R, { hScale: 1.2, landmarks: [{ t: 'super', x: 0.25, w: 20, h: 300 }, { t: 'super', x: 0.36, w: 26, h: 270 }, { t: 'super', x: 0.7, w: 22, h: 320 }] }), par: 0.08, y: 40 },
        { cv: parkTrees(1600, 300, L, R, { autumn: true }), par: 0.38, y: 18 },
        { cv: stoneWall(600, 40, L, R), par: 0.8, y: 0 },
        { cv: props(1280, 240, L, R, 'park'), par: 0.94, y: -1 }
      ];
    } },
    { seed: 53, light: 3, fireflies: true, build(L, R, o) {
      return [
        { cv: skyline(1800, 360, L, R, { hScale: 1.2, landmarks: [{ t: 'super', x: 0.2, w: 20, h: 300 }, { t: 'super', x: 0.62, w: 24, h: 320 }, { t: 'empire', x: 0.82 }] }), par: 0.08, y: 40 },
        { cv: parkTrees(1800, 330, L, R, { water: 24, bridge: true }), par: 0.36, y: 22 },
        { cv: lakeEdge(600, 46, L, R), par: 0.8, y: 0 },
        { cv: props(1280, 240, L, R, 'park'), par: 0.94, y: -1 }
      ];
    } }
  ];

  function create(opts) {
    opts = opts || {};
    const cache = [];
    const cabs = [];
    const get = (i) => {
      i = clamp(i | 0, 0, STAGES.length - 1);
      if (!cache[i]) {
        const st = STAGES[i], L = LIGHT[st.light], R = rng(st.seed);
        cache[i] = { layers: st.build(L, R, opts), st };
        cabs[i] = st.cabs ? cab(L) : null;
      }
      return cache[i];
    };
    // build the other stages in idle time so a stage change never stutters
    const warm = (i) => { if (i >= STAGES.length) return; const run = () => { get(i); warm(i + 1); }; (window.requestIdleCallback || ((f) => setTimeout(f, 60)))(run); };

    function drawStage(b, i, a, cam, gb, iw, time) {
      const S = get(i);
      b.globalAlpha = a;
      for (const Ly of S.layers) {
        const w = Ly.cv.width / D, h = Ly.cv.height / D;
        let x = -((((cam * Ly.par) % w) + w) % w);
        x = Math.round(x * D) / D;
        const y = gb - Ly.y - h;
        for (; x < iw; x += w) b.drawImage(Ly.cv, x, y, w, h);
        if (Ly.fx === 'cabs' && cabs[i]) drawCabs(b, cabs[i], cam * Ly.par, gb - Ly.y, iw, time);
      }
      b.globalAlpha = 1;
    }
    function drawCabs(b, img, off, yb, iw, time) {
      const w = img.width / D * 0.78, h = img.height / D * 0.78, span = iw + 140;
      const lanes = [{ dir: 1, v: 26, y: yb - 9.5, ph: 0 }, { dir: -1, v: 70, y: yb - 2.5, ph: 0.55 }];
      for (const ln of lanes) for (let k = 0; k < 2; k++) {
        let x = (ln.ph * span + k * span * 0.5 + ln.dir * ln.v * time - off) % span;
        if (x < 0) x += span;
        x -= 70;
        x = Math.round(x * D) / D;
        b.save();
        if (ln.dir < 0) { b.translate(x + w, 0); b.scale(-1, 1); b.drawImage(img, 0, ln.y - h, w, h); }
        else b.drawImage(img, x, ln.y - h, w, h);
        b.restore();
      }
    }
    // per-frame life in front of the backdrop: falling leaves (park sunset), fireflies (park night)
    function drawFX(b, i, a, cam, gb, iw, time, accent) {
      const st = STAGES[clamp(i | 0, 0, STAGES.length - 1)];
      if (a <= 0.01) return;
      b.globalAlpha = a;
      if (st.leaves) {
        for (let k = 0; k < 14; k++) {
          const ph = (time * 0.12 + k * 0.071) % 1, x = ((k * 97.3 - cam * 0.9 + Math.sin(time * 1.3 + k) * 10) % (iw + 40) + iw + 40) % (iw + 40) - 20;
          const y = gb - 140 + ph * 150;
          b.fillStyle = k % 3 ? '#e07a2a' : '#f4b040';
          b.fillRect(x, y, 1.5, 1); b.fillRect(x + 0.5 + Math.sin(time * 6 + k) * 0.5, y + 1, 1, 0.5);
        }
      }
      if (st.fireflies) {
        for (let k = 0; k < 18; k++) {
          const x = ((k * 61.7 - cam * 0.7 + Math.sin(time * 0.7 + k * 2.1) * 14) % (iw + 20) + iw + 20) % (iw + 20) - 10;
          const y = gb - 20 - (k * 37 % 70) + Math.sin(time * 1.1 + k) * 6;
          const on = 0.5 + 0.5 * Math.sin(time * 3 + k * 1.7);
          if (on < 0.25) continue;
          b.globalAlpha = a * on;
          b.fillStyle = 'rgba(198,255,61,0.25)'; b.fillRect(x - 1, y - 1, 2.5, 2.5);
          b.fillStyle = accent || '#c6ff3d'; b.fillRect(x - 0.25, y - 0.25, 1, 1);
        }
      }
      b.globalAlpha = 1;
    }

    return {
      count: STAGES.length,
      prepare(i) { get(i); warm(i + 1); },
      // crossfade between two stages: from (fully) and to (alpha t)
      draw(b, from, to, t, cam, gb, iw, time) {
        if (t < 1 || from === to) drawStage(b, from, 1, cam, gb, iw, time);
        if (from !== to && t > 0) drawStage(b, to, t >= 1 ? 1 : t, cam, gb, iw, time);
      },
      fx(b, from, to, t, cam, gb, iw, time, accent) {
        drawFX(b, from, from === to ? 1 : 1 - t, cam, gb, iw, time, accent);
        if (from !== to) drawFX(b, to, t, cam, gb, iw, time, accent);
      }
    };
  }

  window.JawlineRunFighterScenery = { create, D };
})();
